import { useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import type { Issue } from '../api/types'
import { IssueSessionAction } from '../sessions/IssueSessions'
import { StateIcon } from '../components/Icons'
import { InlineText } from '../components/InlineText'
import { IssuePart } from '../issue/IssuePart'
import { IssueProperties } from '../issue/IssueProperties'
import { QuestionCard } from '../questions/QuestionCard'
import { useBoardState, useStore } from '../store/context'
import { questionsOf } from '../store/store'
import { ownColumns, PARTS, placeName } from '../vocab'
import { canInteract, usePortalTarget, useTabScope } from '../workspace/context'
import { cardElement } from './interaction'
import { PeekDiscussion, PeekProposal, PeekSessions } from './PeekWork'

/**
 * Today's enlarged card, on the new model's three parts and open questions.
 * Its title, summary and parts are edited in place; a new issue opens here,
 * its title selected and its description's editor open.
 */
export function Peek({ issue, fresh = false, onClose, onStep, onOpenPage }: {
  issue: Issue; fresh?: boolean; onClose: () => void; onStep: (key: string) => void; onOpenPage: () => void
}) {
  const store = useStore()
  const scope = useTabScope()
  const portal = usePortalTarget()
  const dialog = useRef<HTMLDivElement>(null)
  const previousFocus = useRef(document.activeElement instanceof HTMLElement ? document.activeElement : null)
  const returnTo = useRef(issue.id)
  returnTo.current = issue.id
  const { questions, projects } = useBoardState()
  const asked = questionsOf(questions, issue.id).filter(question => question.state === 'open')

  // The dialog takes the focus, unless a field inside it took it in this commit:
  // a new issue's title, open to type over (Poulpe-800).
  useLayoutEffect(() => {
    const root = dialog.current, active = document.activeElement
    if (!canInteract(scope) || !root) return
    if (active && active !== root && root.contains(active) && active.matches('input, textarea, select, [contenteditable="true"]')) return
    root.focus()
  }, [issue.id, portal, scope])
  useLayoutEffect(() => {
    if (canInteract(scope) && document.activeElement === document.body) dialog.current?.focus()
  })
  useLayoutEffect(() => {
    if (!portal) return
    const previous = previousFocus.current
    const surface = scope?.surface() ?? document.body
    const underneath = [...surface.children].filter(el => !el.classList.contains('board-peek-veil'))
    const inert = underneath.map(el => el.hasAttribute('inert'))
    underneath.forEach(el => el.setAttribute('inert', ''))
    const overflow = surface.style.overflow
    surface.style.overflow = 'hidden'
    return () => {
      underneath.forEach((el, at) => { if (!inert[at]) el.removeAttribute('inert') })
      surface.style.overflow = overflow
      if (!canInteract(scope)) return
      const card = cardElement(returnTo.current, surface)
      if (card) card.focus()
      else if (previous?.isConnected && previous !== document.body && !previous.closest('[inert], [hidden]')) previous.focus()
      else surface.querySelector<HTMLElement>('.summary-new')?.focus()
    }
  }, [portal, scope])

  // Listen after React's controls: an editor or question keeps the keys it handled.
  useLayoutEffect(() => {
    const press = (event: KeyboardEvent) => {
      const root = dialog.current
      if (!canInteract(scope) || !root || !(event.target instanceof HTMLElement) || !root.contains(event.target) || event.defaultPrevented) return
      if (event.target.closest('[role="dialog"]') !== root) return
      if (event.key === 'Escape' && event.target.closest('.popover')?.querySelector('.popover-panel')) return
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); return }
      if (event.key.startsWith('Arrow') && !event.target.closest('input, textarea, select, [contenteditable="true"], .question-card, .popover')) {
        event.preventDefault(); onStep(event.key); return
      }
      const control = event.target.closest('input, textarea, select, button, a, summary, [contenteditable="true"]')
      if (!control) {
        if (event.key === ' ') { event.preventDefault(); onClose(); return }
        if (event.key === 'Enter') { event.preventDefault(); onOpenPage(); return }
      }
      if (event.key !== 'Tab') return
      const items = [...root.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]')]
        .filter(item => item.tabIndex >= 0 && !item.closest('[inert], [hidden]') && !hiddenWithin(item, root))
      const first = items[0], last = items.at(-1)
      if (event.shiftKey && (document.activeElement === first || document.activeElement === root)) { event.preventDefault(); (last ?? root).focus() }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === root)) { event.preventDefault(); (first ?? root).focus() }
    }
    document.addEventListener('keydown', press)
    return () => document.removeEventListener('keydown', press)
  })

  return portal ? createPortal(<div className="board-peek-veil" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
    <div className="board-peek" ref={dialog} role="dialog" aria-modal="true" aria-label={issue.title} tabIndex={-1}>
      <header className="board-peek-head">
        <p><span className="card-key">{issue.key}</span> <span className="state-chip"><StateIcon state={issue.state} />{placeName(issue, ownColumns(projects.get(issue.project)))}</span></p>
        <button type="button" className="button board-peek-close" onClick={onClose}>Close</button>
        <div className="board-peek-title">
          <InlineText key={`title.${issue.id}`} as="h2" value={issue.title} name="title" draftId={`title.${issue.id}`} placeholder="Untitled" maxLength={500} required multiline
            initiallyEditing={fresh} onSave={next => {
              void store.updateIssue(issue.id, { title: next })
              // Enter on a new issue's title goes on to its description.
              if (fresh) requestAnimationFrame(() => {
                const root = dialog.current
                if (root && (document.activeElement === document.body || document.activeElement === root)) root.querySelector<HTMLElement>('textarea[aria-label="Description"]')?.focus()
              })
            }} />
        </div>
        <IssueSessionAction key={`session.${issue.id}`} issue={issue} />
        <InlineText key={`summary.${issue.id}`} as="p" className="issue-summary" value={issue.summary} name="summary" draftId={`summary.${issue.id}`}
          placeholder="Add a one-line summary" maxLength={200} onSave={next => void store.updateIssue(issue.id, { summary: next })} />
        <IssueProperties key={issue.id} issue={issue} />
      </header>
      <div className="board-peek-body" role="region" aria-label={issue.title} tabIndex={0}>
        {asked.length > 0 && <section className="section issue-questions" aria-label="Questions">
          <header className="section-header"><h2>Questions<span className="section-count">{asked.length}</span></h2></header>
          {asked.map(question => <QuestionCard key={question.id} question={question} />)}
        </section>}
        {PARTS.map(part => <IssuePart key={`${issue.id}.${part}`} issue={issue} part={part} initiallyEditing={fresh && part === 'description'} />)}
        <PeekProposal issue={issue} />
        <PeekSessions issue={issue} />
        <PeekDiscussion issue={issue} />
      </div>
      <footer className="board-peek-foot"><span>← → Next card · Space Close · ↩ Open the issue</span>
        <Link className="button" to={`/${encodeURIComponent(issue.key)}`} onClick={event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
          event.preventDefault(); onOpenPage()
        }}>Open the issue</Link>
      </footer>
    </div>
  </div>, portal) : null
}

function hiddenWithin(item: HTMLElement, root: HTMLElement) {
  for (let at: HTMLElement | null = item; at && at !== root; at = at.parentElement) {
    if (getComputedStyle(at).display === 'none' || getComputedStyle(at).visibility === 'hidden') return true
  }
  return false
}
